// src/lib/pairwise.ts — who owes whom, before any netting.
//
// The settle-up screen shows net positions and the minimum transfer set, which
// is what you act on. It is also the thing people disbelieve the first time
// they see it: "Abdallah paid for the hotels and we split them, so I must owe
// him." Both halves of that are true and the conclusion still isn't, because
// he owes you more back.
//
// This reconstructs the gross flows the netting hides. It is derived entirely
// from expenses and settlements already loaded — no query, no new column.
//
// Everything is in integer cents of the trip's base currency. See money.ts.

import { fromCents, toCents } from './money';
import type { ExpenseRow, SettlementRow } from '../data/types';

/** owes[debtorId][creditorId] = cents the debtor owes the creditor, gross. */
export type OwesMatrix = Map<string, Map<string, number>>;

function add(matrix: OwesMatrix, debtor: string, creditor: string, cents: number) {
  if (debtor === creditor || cents === 0) return;
  const row = matrix.get(debtor) ?? new Map<string, number>();
  row.set(creditor, (row.get(creditor) ?? 0) + cents);
  matrix.set(debtor, row);
}

export function buildOwesMatrix(
  expenses: ExpenseRow[],
  settlements: SettlementRow[],
): OwesMatrix {
  const matrix: OwesMatrix = new Map();

  for (const expense of expenses) {
    for (const split of expense.trip_expense_splits) {
      // Whoever paid covered this share, so the member owes the payer for it.
      // A payer's own share is skipped by add(); they did not lend to
      // themselves.
      add(matrix, split.member_id, expense.payer_member_id, toCents(split.base_amount));
    }
  }

  for (const settlement of settlements) {
    // A recorded payment runs the other way: money already moved from -> to,
    // so it cancels what `from` owes, and beyond that becomes credit.
    add(matrix, settlement.to_member_id, settlement.from_member_id, toCents(settlement.base_amount));
  }

  return matrix;
}

export interface PairwisePosition {
  otherId: string;
  /** What the other person owes the subject, gross. */
  theyOwe: number;
  /** What the subject owes the other person, gross. */
  youOwe: number;
  /** theyOwe - youOwe. Positive means the other person owes the subject. */
  net: number;
}

/**
 * The subject's position against every other member, largest imbalance first.
 *
 * Members with nothing either way are dropped — on a trip where two people
 * never shared an expense, a row of zeroes is noise.
 */
export function pairwiseFor(
  subjectId: string,
  memberIds: string[],
  matrix: OwesMatrix,
): PairwisePosition[] {
  const positions: PairwisePosition[] = [];

  for (const otherId of memberIds) {
    if (otherId === subjectId) continue;

    const theyOwe = matrix.get(otherId)?.get(subjectId) ?? 0;
    const youOwe = matrix.get(subjectId)?.get(otherId) ?? 0;

    // Nothing between you, or the two sides cancel exactly — either way the
    // pair contributes nothing to the balance this is explaining, and a row
    // reading "net $0.00" is just something else to read past. Settled pairs
    // are still listed under "Already paid".
    if (theyOwe - youOwe === 0) continue;

    positions.push({
      otherId,
      theyOwe: fromCents(theyOwe),
      youOwe: fromCents(youOwe),
      net: fromCents(theyOwe - youOwe),
    });
  }

  return positions.sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
}

/**
 * The subject's overall balance, summed from the pairwise view.
 *
 * Exists to be checked against trip_member_balances.net_base: if these two ever
 * disagree, the breakdown is lying about the number printed directly above it.
 */
export function netFromPairwise(positions: PairwisePosition[]): number {
  return fromCents(positions.reduce((sum, p) => sum + toCents(p.net), 0));
}
